import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ONBOARDING_TEAM_ROLES,
  type OnboardingRunTargetInput,
  type OnboardingTeamRole,
} from '../../../api/onboardingTypes'
import { useOnboardingTeamLookup } from '../../../api/hooks'
import { INPUT_CLASS, SECONDARY_BUTTON_CLASS } from './ui'

// The team half of the single-partner form: no team, a new team the partner
// owns, or an existing team found through one of its owners or admins.
//
// With a team, the run's credits pay that team's wallet — the server pins it
// at the team step — so the choice is stated next to the credits, not only here.

export type TeamChoiceMode = 'none' | 'personal' | 'create' | 'join' | 'configure'
const TEAM_CHOICE_MODES: readonly TeamChoiceMode[] = ['none', 'personal', 'create', 'join', 'configure']
type BillingMode = 'prepaid' | 'postpaid'
const BILLING_MODES: readonly BillingMode[] = ['prepaid', 'postpaid']

export interface TeamChoice {
  mode: TeamChoiceMode
  /** For `create`. */
  name: string
  /** For `join`: the team picked from the lookup, and the partner's role in it. */
  teamId: string | null
  teamName: string | null
  role: OnboardingTeamRole
  billingMode: BillingMode
  billingPeriodAnchor: string
  billingPeriodMonths: 1 | 3 | 6 | 12
  billingTimezone: string
  existingBillingMode: BillingMode | null
  personalPolicyEmail: string | null
}

export const NO_TEAM: TeamChoice = {
  mode: 'none', name: '', teamId: null, teamName: null, role: 'member',
  billingMode: 'prepaid', billingPeriodAnchor: '', billingPeriodMonths: 1,
  billingTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  existingBillingMode: null, personalPolicyEmail: null,
}

type TeamFields = Pick<OnboardingRunTargetInput, 'team_mode' | 'team_name' | 'join_team_id' | 'team_role' |
  'credit_billing_mode' | 'billing_period_anchor' | 'billing_period_months' | 'billing_timezone'>

function billingFields(choice: TeamChoice): Pick<TeamFields, 'credit_billing_mode' | 'billing_period_anchor' | 'billing_period_months' | 'billing_timezone'> | null {
  if (choice.billingMode === 'prepaid') return { credit_billing_mode: 'prepaid' }
  const anchor = choice.billingPeriodAnchor.trim()
  const timezone = choice.billingTimezone.trim()
  return anchor && timezone ? { credit_billing_mode: 'postpaid', billing_period_anchor: anchor,
    billing_period_months: choice.billingPeriodMonths, billing_timezone: timezone } : null
}

/**
 * The create-run fields for a choice, or null while it is incomplete.
 *
 * Only the fields the mode uses are sent: the server refuses the others rather
 * than dropping them, so a name typed before switching to "join" must not leak.
 */
export function teamChoiceFields(choice: TeamChoice): TeamFields | null {
  if (choice.mode === 'none') return {}
  const billing = billingFields(choice)
  if (billing === null) return null
  if (choice.mode === 'personal') return { team_mode: 'personal', ...billing }
  if (choice.mode === 'create') {
    const name = choice.name.trim()
    return name ? { team_mode: 'create', team_name: name, ...billing } : null
  }
  if (choice.mode === 'configure') return choice.teamId
    ? { team_mode: 'configure', join_team_id: choice.teamId, ...billing } : null
  return choice.teamId
    ? { team_mode: 'join', join_team_id: choice.teamId, team_role: choice.role, ...billing }
    : null
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export default function TeamIntake({
  value,
  onChange,
  partnerEmail,
}: {
  value: TeamChoice
  onChange: (next: TeamChoice) => void
  partnerEmail: string
}) {
  const { t } = useTranslation('pages')
  const [anchorEmail, setAnchorEmail] = useState('')
  // Looked up on an explicit click, not per keystroke: every lookup reads
  // another customer's team list, so it should be a deliberate act.
  const [lookupEmail, setLookupEmail] = useState<string | null>(null)
  const anchorValid = EMAIL_RE.test(anchorEmail.trim())
  const partnerEmailNormalized = EMAIL_RE.test(partnerEmail.trim())
    ? partnerEmail.trim().toLowerCase()
    : null
  const requestedLookupEmail = value.mode === 'personal'
    ? partnerEmailNormalized
    : value.mode === 'join' || value.mode === 'configure'
      ? lookupEmail
      : null
  const lookup = useOnboardingTeamLookup(requestedLookupEmail)
  const lookupError = lookup.error instanceof Error ? lookup.error.message : null
  const personalPolicyLoading = value.mode === 'personal' && (
    value.existingBillingMode === null || value.personalPolicyEmail !== partnerEmailNormalized
  ) && lookupError === null

  const setMode = (mode: TeamChoiceMode) => onChange({
    ...value,
    mode,
    teamId: null,
    teamName: null,
    // Personal mode configures an existing team for an existing account. Keep
    // the form incomplete until its current policy is loaded, so the default
    // can never silently convert postpaid to prepaid.
    billingMode: mode === 'personal' ? 'postpaid' : 'prepaid',
    billingPeriodAnchor: '',
    existingBillingMode: null,
    personalPolicyEmail: null,
  })

  useEffect(() => {
    if (value.mode !== 'personal') return
    if (
      value.existingBillingMode !== null &&
      value.personalPolicyEmail !== partnerEmailNormalized
    ) {
      onChange({
        ...value,
        billingMode: 'postpaid',
        billingPeriodAnchor: '',
        existingBillingMode: null,
        personalPolicyEmail: null,
      })
      return
    }
    if (
      value.existingBillingMode !== null ||
      partnerEmailNormalized === null ||
      !lookup.data ||
      lookup.data.email !== partnerEmailNormalized
    ) return
    const personal = lookup.data.teams.find(
      (team) => team.kind === 'personal' && team.role === 'owner',
    )
    const mode = personal?.credit_billing_mode ?? 'prepaid'
    const next = {
      ...value,
      billingMode: mode,
      billingPeriodAnchor: personal?.billing_period_anchor ?? '',
      billingPeriodMonths: personal?.billing_period_months ?? 1,
      billingTimezone: personal?.billing_timezone ?? value.billingTimezone,
      existingBillingMode: mode,
      personalPolicyEmail: partnerEmailNormalized,
    }
    if (
      value.existingBillingMode !== next.existingBillingMode ||
      value.billingMode !== next.billingMode ||
      value.billingPeriodAnchor !== next.billingPeriodAnchor ||
      value.billingPeriodMonths !== next.billingPeriodMonths ||
      value.billingTimezone !== next.billingTimezone ||
      value.personalPolicyEmail !== next.personalPolicyEmail
    ) {
      onChange(next)
    }
  }, [lookup.data, onChange, partnerEmailNormalized, value])

  return (
    <div className="flex flex-col gap-1.5" data-testid="onboarding-team">
      <span className="text-sm text-foreground">{t('adminOnboarding.team.label')}</span>
      <div className="flex flex-wrap gap-1" role="radiogroup" aria-label={t('adminOnboarding.team.label')}>
        {TEAM_CHOICE_MODES.map((mode) => (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={value.mode === mode}
            onClick={() => setMode(mode)}
            className={`h-8 rounded-lg px-3 text-sm ${
              value.mode === mode
                ? 'bg-surface-3 text-foreground'
                : 'text-muted-foreground hover:bg-surface-2'
            }`}
            data-testid={`onboarding-team-mode-${mode}`}
          >
            {t(`adminOnboarding.team.mode.${mode}`)}
          </button>
        ))}
      </div>
      <span className="text-xs font-medium text-muted-foreground">{t('adminOnboarding.team.help')}</span>

      {value.mode === 'create' && (
        <label className="mt-2 flex flex-col gap-1.5">
          <span className="text-sm text-foreground">{t('adminOnboarding.team.nameLabel')}</span>
          <input
            type="text"
            value={value.name}
            onChange={(e) => onChange({ ...value, name: e.target.value })}
            className={INPUT_CLASS}
            data-testid="onboarding-team-name-input"
          />
          <span className="text-xs font-medium text-muted-foreground">
            {t('adminOnboarding.team.createHelp')}
          </span>
        </label>
      )}

      {(value.mode === 'join' || value.mode === 'configure') && (
        <div className="mt-2 flex flex-col gap-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm text-foreground">{t('adminOnboarding.team.anchorLabel')}</span>
            <div className="flex gap-2">
              <input
                type="email"
                value={anchorEmail}
                onChange={(e) => setAnchorEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && anchorValid) setLookupEmail(anchorEmail.trim().toLowerCase())
                }}
                className={`${INPUT_CLASS} flex-1`}
                data-testid="onboarding-team-anchor-input"
              />
              <button
                type="button"
                disabled={!anchorValid || lookup.isFetching}
                onClick={() => setLookupEmail(anchorEmail.trim().toLowerCase())}
                className={`${SECONDARY_BUTTON_CLASS} h-9`}
                data-testid="onboarding-team-lookup"
              >
                {lookup.isFetching ? t('adminOnboarding.team.lookingUp') : t('adminOnboarding.team.lookup')}
              </button>
            </div>
          </label>

          {lookupError && (
            <p className="text-xs font-medium text-destructive" data-testid="onboarding-team-lookup-error">
              {lookupError}
            </p>
          )}
          {lookup.data && !lookup.data.user_found && (
            <p className="text-xs font-medium text-muted-foreground" data-testid="onboarding-team-no-user">
              {t('adminOnboarding.team.userNotFound', { email: lookup.data.email })}
            </p>
          )}
          {lookup.data && lookup.data.user_found && lookup.data.teams.length === 0 && (
            <p className="text-xs font-medium text-muted-foreground" data-testid="onboarding-team-no-teams">
              {t('adminOnboarding.team.noTeams', { email: lookup.data.email })}
            </p>
          )}
          {lookup.data && lookup.data.teams.length > 0 && (
            <div
              className="flex flex-col gap-1"
              role="radiogroup"
              aria-label={t('adminOnboarding.team.pickLabel')}
              data-testid="onboarding-team-options"
            >
              {lookup.data.teams.map((team) => {
                const full = value.mode === 'join' && team.member_count >= team.member_limit
                const selected = value.teamId === team.team_id
                return (
                  <button
                    key={team.team_id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={full}
                    onClick={() => onChange({ ...value, teamId: team.team_id, teamName: team.name,
                      billingMode: team.credit_billing_mode ?? 'prepaid',
                      billingPeriodAnchor: team.billing_period_anchor ?? '',
                      billingPeriodMonths: team.billing_period_months ?? 1,
                      billingTimezone: team.billing_timezone ?? value.billingTimezone,
                      existingBillingMode: team.credit_billing_mode ?? 'prepaid' })}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50 ${
                      selected
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-border bg-surface-1 text-foreground hover:bg-surface-2'
                    }`}
                    data-testid={`onboarding-team-option-${team.team_id}`}
                  >
                    <span className="flex-1 truncate">{team.name}</span>
                    {team.kind === 'personal' && (
                      <span className="rounded border border-border px-1.5 py-0.5 text-xs text-muted-foreground">
                        {t('adminOnboarding.team.personal')}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {t(`adminOnboarding.team.anchorRole.${team.role}`)}
                    </span>
                    <span className={`text-xs ${full ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {full
                        ? t('adminOnboarding.team.full')
                        : t('adminOnboarding.team.members', {
                            count: team.member_count,
                            limit: team.member_limit,
                          })}
                    </span>
                  </button>
                )
              })}
            </div>
          )}

          {value.teamId && value.mode === 'join' && (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm text-foreground">{t('adminOnboarding.team.roleLabel')}</span>
              <div className="flex gap-1" role="radiogroup" aria-label={t('adminOnboarding.team.roleLabel')}>
                {ONBOARDING_TEAM_ROLES.map((role) => (
                  <button
                    key={role}
                    type="button"
                    role="radio"
                    aria-checked={value.role === role}
                    onClick={() => onChange({ ...value, role })}
                    className={`h-8 rounded-lg px-3 text-sm ${
                      value.role === role
                        ? 'bg-surface-3 text-foreground'
                        : 'text-muted-foreground hover:bg-surface-2'
                    }`}
                    data-testid={`onboarding-team-role-${role}`}
                  >
                    {t(`adminOnboarding.team.role.${role}`)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {value.mode === 'join' && value.teamId && <p className="mt-2 text-xs text-muted-foreground" data-testid="onboarding-team-inherited-billing">
        {t('adminOnboarding.team.inheritedBilling', { mode: t(`adminOnboarding.team.billingMode.${value.billingMode}`) })}
      </p>}

      {personalPolicyLoading && (
        <p role="status" className="mt-3 text-sm text-muted-foreground" data-testid="onboarding-personal-billing-loading">
          {t('adminOnboarding.team.loadingBillingPolicy')}
        </p>
      )}
      {value.mode === 'personal' && lookupError && (
        <p role="alert" className="mt-3 text-xs font-medium text-destructive" data-testid="onboarding-personal-billing-error">
          {lookupError}
        </p>
      )}
      {(value.mode === 'create' || value.mode === 'configure' || (value.mode === 'personal' && !personalPolicyLoading)) && (
        <div className="mt-3 flex flex-col gap-3 rounded-xl border border-border bg-surface-1 p-3" data-testid="onboarding-team-billing">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm text-foreground">{t('adminOnboarding.team.billingMode.label')}</span>
            <div className="flex gap-1" role="radiogroup" aria-label={t('adminOnboarding.team.billingMode.label')}>
              {BILLING_MODES.map((mode) => <button key={mode} type="button" role="radio"
                aria-checked={value.billingMode === mode}
                onClick={() => onChange({ ...value, billingMode: mode })}
                data-testid={`onboarding-billing-mode-${mode}`}
                className={`h-8 rounded-lg px-3 text-sm disabled:cursor-not-allowed disabled:opacity-40 ${value.billingMode === mode ? 'bg-surface-3 text-foreground' : 'text-muted-foreground hover:bg-surface-2'}`}>
                {t(`adminOnboarding.team.billingMode.${mode}`)}
              </button>)}
            </div>
            {value.existingBillingMode === 'postpaid' && value.billingMode === 'prepaid' && (
              <p className="text-xs text-warning" data-testid="onboarding-prepaid-conversion-warning">
                {t('adminOnboarding.team.prepaidConversionWarning')}
              </p>
            )}
          </div>
          {value.billingMode === 'postpaid' && <>
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="flex flex-col gap-1.5"><span className="text-sm text-foreground">{t('adminOnboarding.team.billingAnchor')}</span>
                <input type="date" value={value.billingPeriodAnchor} onChange={(event) => onChange({ ...value, billingPeriodAnchor: event.target.value })}
                  className={INPUT_CLASS} data-testid="onboarding-billing-anchor" /></label>
              <label className="flex flex-col gap-1.5"><span className="text-sm text-foreground">{t('adminOnboarding.team.billingCadence')}</span>
                <select value={value.billingPeriodMonths} onChange={(event) => { const months = Number(event.target.value)
                  if (months === 1 || months === 3 || months === 6 || months === 12) onChange({ ...value, billingPeriodMonths: months }) }}
                  className={INPUT_CLASS} data-testid="onboarding-billing-cadence">
                  {[1, 3, 6, 12].map((months) => <option key={months} value={months}>{t('adminOnboarding.team.billingMonths', { count: months })}</option>)}
                </select></label>
              <label className="flex flex-col gap-1.5"><span className="text-sm text-foreground">{t('adminOnboarding.team.billingTimezone')}</span>
                <input type="text" value={value.billingTimezone} onChange={(event) => onChange({ ...value, billingTimezone: event.target.value })}
                  className={INPUT_CLASS} data-testid="onboarding-billing-timezone" /></label>
            </div>
            <p className="text-xs text-muted-foreground">{t('adminOnboarding.team.postpaidHelp')}</p>
          </>}
        </div>
      )}
    </div>
  )
}
